import React, { useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { X } from "lucide-react";

const FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.QR_CODE,
];

// Full-screen camera barcode scanner. Calls onDetect(code) on each read
// (debounced against repeats) and stays open for rapid back-to-back scanning.
export const CameraScanner = ({ onDetect, onClose, title = "Scan a barcode" }) => {
  const elId = useRef(`cam-${Math.random().toString(36).slice(2, 8)}`);
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: "", at: 0 });

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(elId.current, { formatsToSupport: FORMATS, verbose: false });
    scannerRef.current = scanner;

    const onSuccess = (decodedText) => {
      const now = Date.now();
      if (decodedText === lastRef.current.code && now - lastRef.current.at < 1800) return;
      lastRef.current = { code: decodedText, at: now };
      onDetect?.(decodedText);
    };

    scanner
      .start({ facingMode: "environment" },
        { fps: 10, qrbox: (vw, vh) => { const m = Math.floor(Math.min(vw, vh) * 0.75); return { width: m, height: Math.floor(m * 0.6) }; } },
        onSuccess, () => {})
      .catch((err) => { if (!cancelled) console.error("Camera start failed", err); });

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        try {
          if (s.isScanning) s.stop().then(() => s.clear()).catch(() => {});
          else s.clear();
        } catch (e) { /* already stopped */ }
      }
    };
  }, [onDetect]);

  return (
    <div className="fixed inset-0 z-[95] bg-black flex flex-col" data-testid="camera-scanner">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="font-heading text-lg">{title}</span>
        <button onClick={onClose} data-testid="camera-close" className="inline-flex items-center gap-1 text-white/90 hover:text-brand-yellow"><X className="h-6 w-6" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        <div id={elId.current} className="w-full max-w-md" />
      </div>
      <div className="text-center text-white/70 text-sm pb-6 px-4">Point the camera at a barcode. Scans add automatically — keep scanning or tap ✕ to close.</div>
    </div>
  );
};
